import { useState } from "react";
import type { AppSummary } from "@compath/renderer";
import { metaFor } from "../../models/catalog";

// The marketplace/detail page for one add-on app. Entitled apps can be opened; non-entitled apps
// show the purchase CTA (the app is already deployed — buying only grants access).
export function AppDetail({ app, onOpen }: { app: AppSummary; onOpen: () => void }) {
  const m = metaFor(app.id);
  const [tab, setTab] = useState<"detail" | "screens" | "perm">("detail");

  return (
    <div className="detail">
      <div className="detail-head">
        <div className="detail-icon" style={{ background: m.tint }}>{m.emoji}</div>
        <div className="detail-info">
          <div className="detail-name">
            <h1>{app.name}</h1>
            <span className="mono">{app.id}</span>
            {app.entitled
              ? <span className="badge ok">● 사용 중</span>
              : <span className="badge locked">🔒 미구매</span>}
          </div>
          <div className="meta">{m.publisher} · v1.0.0 · 화면 {m.screens.length}개</div>
          <div className="tags">
            {m.category.split(" · ").map((t) => <span key={t} className="tag">{t}</span>)}
          </div>
        </div>
        <div className="detail-actions">
          {m.priceLabel && !app.entitled && (
            <div className="price">{m.priceLabel.split(" / ")[0]}<small> / {m.priceLabel.split(" / ")[1]}</small></div>
          )}
          {app.entitled ? (
            <button className="btn primary" onClick={onOpen}>열기</button>
          ) : (
            <button
              className="btn primary"
              onClick={() => { if (app.purchaseUrl) window.location.assign(app.purchaseUrl); else alert("구매 흐름은 커머스 연동 후 활성화됩니다."); }}
            >
              구매하고 설치
            </button>
          )}
          <button className="btn ghost">미리보기</button>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === "detail" ? "active" : ""}`} onClick={() => setTab("detail")}>세부정보</button>
        <button className={`tab ${tab === "screens" ? "active" : ""}`} onClick={() => setTab("screens")}>포함 화면</button>
        <button className={`tab ${tab === "perm" ? "active" : ""}`} onClick={() => setTab("perm")}>권한 · entitlement</button>
      </div>

      <div className="detail-body">
        <div className="detail-main">
          {tab !== "perm" && (
            <>
              <h3>소개</h3>
              <p>{m.description}</p>
            </>
          )}
          {tab === "detail" && (
            <div className="shots">
              {(m.screens.length ? m.screens : [{ label: "미리보기" }]).slice(0, 3).map((s, i) => (
                <div className="shot" key={i}>🖼 {"label" in s ? s.label : ""}</div>
              ))}
            </div>
          )}
          {(tab === "detail" || tab === "screens") && (
            <>
              <h3>제공 화면</h3>
              {m.screens.length === 0 && <p>등록된 화면이 없습니다.</p>}
              {m.screens.map((s) => (
                <div className="screen-row" key={s.id}>
                  <span className="screen-id">{s.id}</span>
                  <span>{s.label}</span>
                </div>
              ))}
            </>
          )}
          {tab === "perm" && (
            <>
              <h3>권한 · entitlement</h3>
              <p>
                이 앱은 항상 클러스터에 배포·연결되어 있으며, 접근은 entitlement(“구매”)로만 게이트됩니다.
                sibling은 compath BFF가 주입하는 신원 헤더(X-Compath-User-Id / User / Groups)를 신뢰합니다.
              </p>
            </>
          )}
        </div>

        <aside className="aside">
          <div className="info-card">
            <h4>정보</h4>
            <div className="kv"><span>카테고리</span><span>{m.category}</span></div>
            <div className="kv"><span>sibling</span><span className="mono">{app.id}</span></div>
            <div className="kv"><span>배포 상태</span><span style={{ color: "var(--green)" }}>연결됨</span></div>
            <div className="kv"><span>내 권한</span><span style={{ color: app.entitled ? "var(--green)" : "var(--amber)" }}>{app.entitled ? "사용 중" : "미구매"}</span></div>
          </div>
          {!app.entitled && (
            <div className="note">
              <b>앱은 이미 클러스터에 배포돼 있어요.</b><br />
              구매하면 접근 권한(entitlement)만 부여되고 바로 열립니다.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
